import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Public API instance
export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Admin API instance with basic auth
export const adminApi = axios.create({
  baseURL: `${BACKEND_URL}/api/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Shopify API instance
export const shopifyApi = axios.create({
  baseURL: `${BACKEND_URL}/api/shopify`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Set admin credentials
export const setAdminCredentials = (password) => {
  const credentials = btoa(`admin:${password}`);
  adminApi.defaults.headers.common['Authorization'] = `Basic ${credentials}`;
  localStorage.setItem('adminAuth', credentials);
};

// Load saved credentials
export const loadAdminCredentials = () => {
  const saved = localStorage.getItem('adminAuth');
  if (saved) {
    adminApi.defaults.headers.common['Authorization'] = `Basic ${saved}`;
    return true;
  }
  return false;
};

// Clear credentials
export const clearAdminCredentials = () => {
  delete adminApi.defaults.headers.common['Authorization'];
  localStorage.removeItem('adminAuth');
};

// ============== PUBLIC API ==============

export const getQuizQuestions = async () => {
  const response = await api.get('/quiz/questions');
  return response.data.questions;
};

export const submitQuiz = async (answers, email = null, source = 'web') => {
  const response = await api.post(`/quiz/submit?source=${source}`, { answers, email });
  return response.data;
};

export const getProducts = async () => {
  const response = await api.get('/products');
  return response.data.products;
};

export const getProduct = async (productId) => {
  const response = await api.get(`/products/${productId}`);
  return response.data;
};

export const getPublicSettings = async () => {
  const response = await api.get('/settings');
  return response.data;
};

// ============== SHOPIFY API ==============

export const getShopifyProduct = async (handle) => {
  const response = await shopifyApi.get(`/product/${handle}`);
  return response.data;
};

export const getShopifyProducts = async (handles) => {
  const response = await shopifyApi.get(`/products?handles=${handles.join(',')}`);
  return response.data.products;
};

export const createShopifyCart = async (variantIds) => {
  const response = await shopifyApi.post('/cart/create', variantIds);
  return response.data;
};

export const getShopifyConfig = async () => {
  const response = await shopifyApi.get('/config');
  return response.data;
};

// ============== ADMIN API ==============

export const adminGetStats = async () => {
  const response = await adminApi.get('/stats');
  return response.data;
};

export const adminGetSettings = async () => {
  const response = await adminApi.get('/settings');
  return response.data;
};

export const adminUpdateSettings = async (data) => {
  const response = await adminApi.put('/settings', data);
  return response.data;
};

export const adminGetQuizResponses = async (limit = 100, skip = 0, source = null) => {
  let url = `/quiz-responses?limit=${limit}&skip=${skip}`;
  if (source) url += `&source=${source}`;
  const response = await adminApi.get(url);
  return response.data;
};

export const adminGetQuizResponse = async (id) => {
  const response = await adminApi.get(`/quiz-responses/${id}`);
  return response.data;
};

export const adminGetConditionAnalytics = async () => {
  const response = await adminApi.get('/analytics/conditions');
  return response.data;
};

export const adminGetSymptomAnalytics = async () => {
  const response = await adminApi.get('/analytics/symptoms');
  return response.data;
};

export const adminGetSystems = async () => {
  const response = await adminApi.get('/systems');
  return response.data.systems;
};

export const adminCreateSystem = async (data) => {
  const response = await adminApi.post('/systems', data);
  return response.data;
};

export const adminUpdateSystem = async (id, data) => {
  const response = await adminApi.put(`/systems/${id}`, data);
  return response.data;
};

export const adminDeleteSystem = async (id) => {
  const response = await adminApi.delete(`/systems/${id}`);
  return response.data;
};

export const adminGetConditions = async () => {
  const response = await adminApi.get('/conditions');
  return response.data.conditions;
};

export const adminCreateCondition = async (data) => {
  const response = await adminApi.post('/conditions', data);
  return response.data;
};

export const adminUpdateCondition = async (id, data) => {
  const response = await adminApi.put(`/conditions/${id}`, data);
  return response.data;
};

export const adminDeleteCondition = async (id) => {
  const response = await adminApi.delete(`/conditions/${id}`);
  return response.data;
};

export const adminGetProducts = async (search = null) => {
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  const response = await adminApi.get(`/products${params}`);
  return response.data.products;
};

export const adminCreateProduct = async (data) => {
  const response = await adminApi.post('/products', data);
  return response.data;
};

export const adminUpdateProduct = async (id, data) => {
  const response = await adminApi.put(`/products/${id}`, data);
  return response.data;
};

export const adminDeleteProduct = async (id) => {
  const response = await adminApi.delete(`/products/${id}`);
  return response.data;
};

export const adminSyncShopifyProducts = async () => {
  const response = await adminApi.post('/shopify-sync');
  return response.data;
};

// Protocol Builder APIs
export const adminGetProtocolBuilder = async (protocolId) => {
  const response = await adminApi.get(`/protocol-builder/${protocolId}`);
  return response.data;
};

export const adminUpdateProtocolProducts = async (protocolId, products) => {
  const response = await adminApi.put(`/protocol-builder/${protocolId}/products`, products);
  return response.data;
};

export const adminDuplicateProtocol = async (protocolId, newName, conditionId = null) => {
  const params = new URLSearchParams({ new_name: newName });
  if (conditionId) params.append('condition_id', conditionId);
  const response = await adminApi.post(`/protocol-builder/duplicate/${protocolId}?${params}`);
  return response.data;
};

export const adminGetProductUsage = async () => {
  const response = await adminApi.get('/product-usage');
  return response.data;
};

export const adminGetProductVendors = async () => {
  const response = await adminApi.get('/products/vendors');
  return response.data;
};

// Cross-sell APIs
export const adminGetCrossSell = async (conditionId) => {
  const response = await adminApi.get(`/cross-sell/${conditionId}`);
  return response.data;
};

export const adminUpdateCrossSell = async (conditionId, products) => {
  const response = await adminApi.put(`/cross-sell/${conditionId}`, products);
  return response.data;
};

export const adminGetAllCrossSell = async () => {
  const response = await adminApi.get('/cross-sell-all');
  return response.data;
};

export const adminGetProtocols = async () => {
  const response = await adminApi.get('/protocols');
  return response.data.protocols;
};

export const adminCreateProtocol = async (data) => {
  const response = await adminApi.post('/protocols', data);
  return response.data;
};

export const adminUpdateProtocol = async (id, data) => {
  const response = await adminApi.put(`/protocols/${id}`, data);
  return response.data;
};

export const adminDeleteProtocol = async (id) => {
  const response = await adminApi.delete(`/protocols/${id}`);
  return response.data;
};

export const adminGetProtocolProducts = async (protocolId = null) => {
  const params = protocolId ? `?protocol_id=${protocolId}` : '';
  const response = await adminApi.get(`/protocol-products${params}`);
  return response.data.protocol_products;
};

export const adminCreateProtocolProduct = async (data) => {
  const response = await adminApi.post('/protocol-products', data);
  return response.data;
};

export const adminDeleteProtocolProduct = async (id) => {
  const response = await adminApi.delete(`/protocol-products/${id}`);
  return response.data;
};

export const adminGetQuizQuestions = async () => {
  const response = await adminApi.get('/quiz-questions');
  return response.data.questions;
};

export const adminCreateQuizQuestion = async (data) => {
  const response = await adminApi.post('/quiz-questions', data);
  return response.data;
};

export const adminUpdateQuizQuestion = async (id, data) => {
  const response = await adminApi.put(`/quiz-questions/${id}`, data);
  return response.data;
};

export const adminDeleteQuizQuestion = async (id) => {
  const response = await adminApi.delete(`/quiz-questions/${id}`);
  return response.data;
};

export const adminGetQuizAnswers = async (questionId = null) => {
  const params = questionId ? `?question_id=${questionId}` : '';
  const response = await adminApi.get(`/quiz-answers${params}`);
  return response.data.answers;
};

export const adminCreateQuizAnswer = async (data) => {
  const response = await adminApi.post('/quiz-answers', data);
  return response.data;
};

export const adminUpdateQuizAnswer = async (id, data) => {
  const response = await adminApi.put(`/quiz-answers/${id}`, data);
  return response.data;
};

export const adminDeleteQuizAnswer = async (id) => {
  const response = await adminApi.delete(`/quiz-answers/${id}`);
  return response.data;
};

export const adminGetAnswerRules = async (questionId = null) => {
  const params = questionId ? `?question_id=${questionId}` : '';
  const response = await adminApi.get(`/answer-rules${params}`);
  return response.data.rules;
};

export const adminCreateAnswerRule = async (data) => {
  const response = await adminApi.post('/answer-rules', data);
  return response.data;
};

export const adminUpdateAnswerRule = async (id, data) => {
  const response = await adminApi.put(`/answer-rules/${id}`, data);
  return response.data;
};

export const adminDeleteAnswerRule = async (id) => {
  const response = await adminApi.delete(`/answer-rules/${id}`);
  return response.data;
};

export const adminGetResultTemplates = async () => {
  const response = await adminApi.get('/result-templates');
  return response.data.templates;
};

export const adminCreateResultTemplate = async (data) => {
  const response = await adminApi.post('/result-templates', data);
  return response.data;
};

export const adminUpdateResultTemplate = async (id, data) => {
  const response = await adminApi.put(`/result-templates/${id}`, data);
  return response.data;
};

export const adminDeleteResultTemplate = async (id) => {
  const response = await adminApi.delete(`/result-templates/${id}`);
  return response.data;
};

export const adminGetEmails = async () => {
  const response = await adminApi.get('/email-captures');
  return response.data.emails;
};

// ============== ANALYTICS EVENT TRACKING ==============

export const trackAnalyticsEvent = async (eventType, data = {}) => {
  try {
    await api.post('/analytics/event', {
      event_type: eventType,
      ...data,
    });
  } catch (error) {
    // Silent fail - don't block UX for analytics
    console.warn('Analytics event failed:', error);
  }
};

export const adminGetAnalyticsOverview = async () => {
  const response = await adminApi.get('/analytics/overview');
  return response.data;
};

// Granular exports
export const adminExportProtocols = async () => {
  const response = await adminApi.get('/export/protocols');
  return response.data;
};

export const adminExportCrossSell = async () => {
  const response = await adminApi.get('/export/cross-sell');
  return response.data;
};

export const adminExportScoringRules = async () => {
  const response = await adminApi.get('/export/scoring-rules');
  return response.data;
};

export const adminImportJson = async (data) => {
  const response = await adminApi.post('/import/json', data);
  return response.data;
};

export const adminExportData = async () => {
  const response = await adminApi.get('/export');
  return response.data;
};
